import { set } from "lodash";
import "./TableCreator.css";
import { Button, CircularProgress } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getComponentsImport, getSetupImport } from "src/components/gametable/projectImports";
import { DataSheet, DeckInfo, TableOptions, TableSelectOptions, TableSelectOptionValue } from "src/components/types";
import { useImmer } from "use-immer";

type TableCreatorProps = {
  roles?: {
    designer: (string | null)[];
    mod: (string | null)[];
    player: (string | null)[];
  };
  projectSlug?: string;
  options?: TableSelectOptions;
  allAccess?: boolean;
  tryErasePreviousTable: () => void;
};

export default function TableCreator({
  roles,
  projectSlug,
  options,
  allAccess,
  tryErasePreviousTable,
}: TableCreatorProps) {
  const tableModeLabels: { [key: string]: string } = {
    "public-sandbox": "Sandbox",
    "private-sandbox": "Private Sandbox",
    "competitive-edit": "Standard",
    "demo-invite": "Demo Invite",
    competitive: "Competitive",
  };

  const [tableMode, setTableMode] = useState(localStorage.getItem("table-mode-" + projectSlug) ?? "competitive-edit");
  const [isPrivate, setIsPrivate] = useState(false); // private is keyword
  const [optionsReset, setOptionsReset] = useState(false);
  const [tableOptions, setTableOptions] = useImmer<TableOptions>({
    numPlayers: +(localStorage.getItem("table-num-players-" + projectSlug) ?? 0),
    selectedColors: JSON.parse(localStorage.getItem("table-selected-colors-" + projectSlug) ?? "[]"),
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [progressIndicatorVisible, setProgressIndicatorVisible] = useState(false);

  const gameSetup = useRef<any>();
  const gameComponents = useRef<any>();

  const allColors = useMemo<string[]>(() => options?.playerColors, [options]);

  const selectNumPlayers = useCallback(
    (evt: { target: { value: string | number } }) => {
      let newAmt = +evt.target.value;
      setTableOptions((options) => {
        let colors = [...(options.selectedColors ?? [])];
        let result = [...(options.selectedColors ?? [])];
        if (colors.length > newAmt) {
          result = colors.slice(0, newAmt);
        }
        let newColors = colors.slice();
        if (newColors.length < newAmt) {
          for (let color of allColors ?? []) {
            if (!newColors.includes(color)) {
              newColors.push(color);
              if (newColors.length === newAmt) {
                result = newColors;
                break;
              }
            }
          }
        }
        options.numPlayers = newAmt;
        options.selectedColors = result;
        localStorage.setItem("table-num-players-" + projectSlug, String(newAmt));
        localStorage.setItem("table-selected-colors-" + projectSlug, JSON.stringify(result));
      });
    },
    [allColors, setTableOptions],
  );

  let tableModeOptions = [];
  let possibleValues = ["competitive-edit", "public-sandbox"];
  if (
    localStorage.getItem("user-role")?.includes("admin") ||
    roles?.designer?.includes(localStorage.getItem("user-name")) ||
    localStorage.getItem("user-role")?.includes("cgestaff") ||
    roles?.mod?.includes(localStorage.getItem("user-name"))
  ) {
    possibleValues.push("demo-invite");
  }
  for (let value of possibleValues) {
    tableModeOptions.push(
      <option value={value} key={"mode-" + value}>
        {tableModeLabels[value]}
      </option>,
    );
  }

  let tableOptionsElems = [];
  let defaults: { [key: string]: string | boolean } = {};
  for (let optionName in options) {
    if (options[optionName].defaultBool) {
      defaults[optionName] = options[optionName].defaultBool;
    } else if (options[optionName].default) {
      defaults[optionName] = options[optionName].default;
    } else if (options[optionName].type === "checkbox") {
      defaults[optionName] = false;
    } else if (options[optionName].values && Object.keys(options[optionName].values).length > 0) {
      let sortedOptions = Object.entries(
        (options[optionName].values as { [key: string]: TableSelectOptionValue }) ?? {},
      ).toSorted((a, b) => (a[1].displayOrder ?? 0) - (b[1].displayOrder ?? 0));
      defaults[optionName] = sortedOptions[0][0];
    }
  }

  useEffect(() => {
    setTableOptions((options) => {
      return { numPlayers: options.numPlayers, selectedColors: options.selectedColors };
    });
    for (let optionName in options) {
      let storageValue: string | boolean | null = null;
      if (
        (storageValue = localStorage.getItem("selected-option-" + projectSlug + ":" + optionName)) &&
        (options[optionName].type === "checkbox" || options[optionName]?.values?.[storageValue]) &&
        (!options[optionName].hidden || allAccess) &&
        (!options[optionName].values[storageValue]?.hidden || allAccess)
      ) {
        setTableOptions((options: TableOptions) => {
          if (storageValue === "true") {
            // localstorage only stores strings, thanks
            storageValue = true;
          }
          if (storageValue === "false") {
            storageValue = false;
          }
          options[optionName] = storageValue;
          return options;
        });
      }
    }
  }, [optionsReset]);

  function setOption(evt: React.ChangeEvent) {
    let target = evt.target as HTMLInputElement;
    let newValue = target.checked ?? target.value;
    let optionName = target.name;
    let storageValue = String(newValue);
    localStorage.setItem("selected-option-" + projectSlug + ":" + optionName, storageValue);
    setOptionsReset(false);
    setTableOptions((options: TableOptions) => {
      options[optionName] = newValue;
    });
  }

  function selectColor(evt: React.ChangeEvent<HTMLInputElement>) {
    let target = evt.target as HTMLInputElement;
    let color = target.name;
    console.log(color, target.value, evt);
    let newAmt = 0;
    setTableOptions((options: TableOptions) => {
      let colors = [...(options.selectedColors ?? [])];
      if (colors.includes(color)) {
        let index = colors.indexOf(color);
        newAmt = colors.length - 1;
        options.numPlayers = newAmt;
        options.selectedColors = [...colors.slice(0, index), ...colors.slice(index + 1)];
        localStorage.setItem("table-num-players-" + projectSlug, String(newAmt));
        localStorage.setItem("table-selected-colors-" + projectSlug, JSON.stringify(options.selectedColors));
      } else {
        newAmt = colors.length + 1;
        options.numPlayers = newAmt;
        options.selectedColors = colors.concat([color]);
        localStorage.setItem("table-num-players-" + projectSlug, String(newAmt));
        localStorage.setItem("table-selected-colors-" + projectSlug, JSON.stringify(options.selectedColors));
      }
    });
  }

  function checkOptionCondition(
    condition: { [key: string]: any },
    allOptions: { [key: string]: any },
    defaults: { [key: string]: any } = {},
  ) {
    if (condition) {
      for (let otherOption in condition) {
        let eq = condition[otherOption].eq;
        let ne = condition[otherOption].ne;
        let targetValue = allOptions[otherOption] || defaults[otherOption];
        if (eq && eq !== targetValue) {
          return false;
        }
        if (ne && ne === targetValue) {
          return false;
        }
      }
    }
    return true;
  }

  for (let [optionName, option] of Object.entries(options ?? ({} as { [key: string]: TableSelectOptions }))
    .filter((a) => a[0] !== "playerColors")
    .toSorted((a, b) => +a[1].displayOrder - b[1].displayOrder)) {
    if (optionName === "playerColors") {
      continue;
    }
    let options = [];
    if (!checkOptionCondition(option.condition, tableOptions, defaults)) {
      continue;
    }

    let currentValue = tableOptions[optionName] ?? option.default ?? defaults[optionName];

    for (let [valueName, valueInfo] of Object.entries(
      (option.values as { [key: string]: TableSelectOptionValue }) ?? {},
    ).toSorted((a, b) => (a[1].displayOrder ?? 0) - (b[1].displayOrder ?? 0))) {
      if (valueInfo.hidden && !allAccess) {
        continue;
      }
      options.push(
        <option value={valueName} key={valueName}>
          {valueInfo.displayName}
        </option>,
      );
    }
    let hidden = option.hidden && !allAccess;
    let inputEl;
    switch (option.type) {
      case "checkbox":
        inputEl = (
          <input key={optionName} type="checkbox" name={optionName} onChange={setOption} checked={currentValue}></input>
        );
        break;
      default:
        inputEl = (
          <select
            key={optionName}
            name={optionName}
            onChange={setOption}
            value={currentValue}
            className="table-creator-options-select"
          >
            {options}
          </select>
        );
    }

    let optionStyle = Object.assign(
      { display: hidden ? "none" : "", opacity: option.hidden ? 0.5 : "unset" },
      option.customStyle,
    );
    let optionLabel = (
      <div style={optionStyle} key={"label-" + optionName}>
        <label htmlFor={optionName}>{option.displayName}: </label>
      </div>
    );
    tableOptionsElems.push(optionLabel);
    let optionInput = (
      <div style={optionStyle} key={"input-" + optionName}>
        {inputEl}
      </div>
    );
    tableOptionsElems.push(optionInput);
  }

  function isInstaStart() {
    return tableMode?.includes("sandbox") || tableMode === "demo-invite";
  }

  useEffect(() => {
    if (projectSlug) {
      gameSetup.current = undefined;
      window.componentPool = undefined;
      getSetupImport(projectSlug)
        .then((module) => {
          gameSetup.current = module.default;
          if (
            localStorage.getItem("table-num-players-" + projectSlug) === null &&
            localStorage.getItem("table-selected-colors-" + projectSlug) === null
          ) {
            selectNumPlayers({ target: { value: 2 } });
          }
        })
        .catch((err) => {
          console.log(err);
          return;
        });
      getComponentsImport(projectSlug)
        .then((module) => {
          gameComponents.current = module.default;
          window.componentPool = module.default;
        })
        .catch((err) => {
          console.log(err);
          return;
        });
    }
  }, [projectSlug, selectNumPlayers, allColors, setTableOptions]);

  const createNewTable = () => {
    console.log("creating new table for project", projectSlug);
    setTableOptions((options) => {
      let completeOptions = Object.assign(defaults, options) as TableOptions;
      let components = gameComponents.current;
      fetch("/api/projects/" + projectSlug + "/new-table", {
        method: "POST",
        body: JSON.stringify({
          start_position: { options: completeOptions },
          tableMode,
          isPrivate,
          components,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      }).then((response) => {
        if (response.ok) {
          setProgressIndicatorVisible(false);
          return response.json();
        }
        return Promise.reject(response);
      });
    });
  };

  function createButtonClickHandler(): void {
    setProgressIndicatorVisible(true);
    setErrorMessage("");
    tryErasePreviousTable();
    createNewTable();
  }

  let checkboxes = [];
  for (let color of allColors ?? []) {
    let checkbox = (
      <input
        type="checkbox"
        id={color}
        name={color}
        key={"checkbox-" + color}
        checked={tableOptions.selectedColors?.includes(color)}
        onChange={selectColor}
      ></input>
    );

    checkboxes.push(checkbox);
    checkboxes.push(
      <label htmlFor={color} key={"label-" + color}>
        {color}
      </label>,
    );
    checkboxes.push(<br />);
  }

  let playerOptions = [];
  for (let i = 0; i <= 4; ++i) {
    playerOptions.push(
      <option value={i} key={"option-" + i}>
        {i}
      </option>,
    );
  }

  function changeTableModeHandler(evt: React.ChangeEvent<HTMLSelectElement>) {
    setTableMode(evt.target.value);
    localStorage.setItem("table-mode-" + projectSlug, evt.target.value);
  }

  return (
    <div className="table-creator">
      <h3>New table settings</h3>
      <label htmlFor="tableMode">Table mode: </label>
      <select name="tableMode" value={tableMode} onChange={changeTableModeHandler}>
        {tableModeOptions}
      </select>
      <br />
      <label htmlFor="private">Private table</label>
      <input name="tableMode" type="checkbox" onChange={(e) => setIsPrivate(e.target.checked)}></input>
      {isPrivate ? (
        <span
          style={{
            textDecoration: "italics",
            fontSize: "0.8em",
            color: "gray",
          }}
        >
          Private tables (and any chat messages in them) are visible by project mods, and anyone who gets (or guesses)
          the table link.
        </span>
      ) : (
        <></>
      )}
      <br />
      {isInstaStart() ? (
        <>
          <label htmlFor="numPlayers">Number of players: </label>
          <select name="numPlayers" value={tableOptions.numPlayers} onChange={selectNumPlayers}>
            {playerOptions}
          </select>
          <fieldset>{checkboxes}</fieldset>
        </>
      ) : (
        <></>
      )}
      <Button
        onClick={() => {
          for (let optionName in options) {
            localStorage.removeItem("selected-option-" + projectSlug + ":" + optionName);
          }
          setOptionsReset(true);
        }}
      >
        Reset to default options
      </Button>
      <div className="table-creator-options">{tableOptionsElems}</div>
      <br />
      <div className="table-creator-button-progress-container">
        <button className="create-table-button" onClick={() => createButtonClickHandler()}>
          Create new table
        </button>
        {progressIndicatorVisible ? <CircularProgress /> : null}
      </div>
      <div className="error-message">{errorMessage}</div>
    </div>
  );
}
